import {
  type ClientActivityDetails,
  type ClientActivityEntry,
  type ClientActivitySend,
  type ClientActivityStorage,
  ClientActivityType,
} from "./types";

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

export const SESSION_IDLE_TIMEOUT_MS = 30 * MINUTE_MS;
export const FLUSH_DEBOUNCE_MS = 5 * 1000;
export const MAX_ENTRIES_PER_REQUEST = 20;
export const MAX_QUEUED_ENTRIES = 100;
export const MAX_SIGNED_OUT_BUFFER_ENTRIES = 50;
export const MAX_ENTRY_AGE_MS = 30 * DAY_MS;
export const MAX_PROPERTY_COUNT = 4;
export const MAX_PROPERTY_VALUE_LENGTH = 200;

export const SIGNED_OUT_BUFFER_KEY_PREFIX = "clientActivity.signedOutBuffer.";
export const LAST_ACTIVITY_KEY_PREFIX = "clientActivity.lastActivityAt.";

export interface ClientActivityReporterDeps {
  send: ClientActivitySend;
  now: () => Date;
  storage: ClientActivityStorage;
  newId: () => string;
  platformId: string;
  isAuthenticated: () => boolean;
  onError: (error: unknown) => void;
  onSessionStart: () => void;
}

export interface ClientActivityReporter {
  record(type: ClientActivityType, details?: ClientActivityDetails): void;
  flush(options: { keepalive: boolean }): Promise<void>;
}

const KNOWN_TYPES: readonly string[] = Object.values(ClientActivityType);

function isBufferedEntry(value: unknown): value is ClientActivityEntry {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.type === "string" &&
    KNOWN_TYPES.includes(candidate.type) &&
    typeof candidate.occurredAtUtc === "string" &&
    typeof candidate.beforeSignIn === "boolean"
  );
}

function stripQueryAndHash(route: string): string {
  const end = route.search(/[?#]/);
  return end === -1 ? route : route.slice(0, end);
}

function capProperties(properties: Record<string, string>): Record<string, string> | undefined {
  const capped = Object.entries(properties)
    .slice(0, MAX_PROPERTY_COUNT)
    .map(([key, value]): [string, string] => [key, value.slice(0, MAX_PROPERTY_VALUE_LENGTH)]);
  return capped.length === 0 ? undefined : Object.fromEntries(capped);
}

export function createClientActivityReporter(
  deps: ClientActivityReporterDeps,
): ClientActivityReporter {
  const { send, now, storage, newId, platformId, isAuthenticated, onError, onSessionStart } = deps;
  const bufferKey = `${SIGNED_OUT_BUFFER_KEY_PREFIX}${platformId}`;
  const lastActivityKey = `${LAST_ACTIVITY_KEY_PREFIX}${platformId}`;

  let queue: ClientActivityEntry[] = [];
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let lastActivityMs: number | undefined = readLastActivityMs();

  function readStorage(key: string): string | null {
    try {
      return storage.getItem(key);
    } catch (error) {
      onError(error);
      return null;
    }
  }

  function writeStorage(key: string, value: string): void {
    try {
      storage.setItem(key, value);
    } catch (error) {
      onError(error);
    }
  }

  function removeStorage(key: string): void {
    try {
      storage.removeItem(key);
    } catch (error) {
      onError(error);
    }
  }

  function readLastActivityMs(): number | undefined {
    const raw = readStorage(lastActivityKey);
    const parsed = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  function readSignedOutBuffer(): ClientActivityEntry[] {
    const raw = readStorage(bufferKey);
    if (raw === null) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) throw new Error("Signed-out activity buffer is not an array");
      return parsed.filter(isBufferedEntry);
    } catch (error) {
      onError(error);
      return [];
    }
  }

  function appendToSignedOutBuffer(entry: ClientActivityEntry): void {
    const buffer = [...readSignedOutBuffer(), entry].slice(-MAX_SIGNED_OUT_BUFFER_ENTRIES);
    writeStorage(bufferKey, JSON.stringify(buffer));
  }

  function adoptSignedOutBuffer(): void {
    const queuedIds = new Set(queue.map((entry) => entry.id));
    const adopted = readSignedOutBuffer().filter((entry) => !queuedIds.has(entry.id));
    if (adopted.length === 0) return;
    queue = [...adopted, ...queue].slice(-MAX_QUEUED_ENTRIES);
  }

  function releaseSentFromSignedOutBuffer(sent: ClientActivityEntry[]): void {
    const sentIds = new Set(sent.map((entry) => entry.id));
    const remaining = readSignedOutBuffer().filter((entry) => !sentIds.has(entry.id));
    if (remaining.length === 0) {
      removeStorage(bufferKey);
      return;
    }
    writeStorage(bufferKey, JSON.stringify(remaining));
  }

  function dropExpiredEntries(): void {
    const oldestAllowedMs = now().getTime() - MAX_ENTRY_AGE_MS;
    queue = queue.filter((entry) => Date.parse(entry.occurredAtUtc) >= oldestAllowedMs);
  }

  function clearDebounce(): void {
    if (debounceTimer === undefined) return;
    clearTimeout(debounceTimer);
    debounceTimer = undefined;
  }

  function scheduleFlush(): void {
    clearDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      void flush({ keepalive: false });
    }, FLUSH_DEBOUNCE_MS);
  }

  async function flush({ keepalive }: { keepalive: boolean }): Promise<void> {
    clearDebounce();
    if (!isAuthenticated()) return;
    adoptSignedOutBuffer();
    dropExpiredEntries();
    while (queue.length > 0) {
      const chunk = queue.splice(0, MAX_ENTRIES_PER_REQUEST);
      try {
        await send(chunk, { keepalive });
        releaseSentFromSignedOutBuffer(chunk);
      } catch (error) {
        onError(error);
        queue = [...chunk, ...queue].slice(-MAX_QUEUED_ENTRIES);
        return;
      }
    }
  }

  function record(type: ClientActivityType, details: ClientActivityDetails = {}): void {
    const occurredAt = now();
    const occurredAtMs = occurredAt.getTime();
    const startsNewSession =
      lastActivityMs === undefined || occurredAtMs - lastActivityMs > SESSION_IDLE_TIMEOUT_MS;
    lastActivityMs = occurredAtMs;
    writeStorage(lastActivityKey, String(occurredAtMs));
    if (startsNewSession) onSessionStart();
    if (type === ClientActivityType.AppOpen && !startsNewSession) return;

    const entry: ClientActivityEntry = {
      id: newId(),
      type,
      occurredAtUtc: occurredAt.toISOString(),
      beforeSignIn: !isAuthenticated(),
    };
    if (details.route !== undefined) entry.route = stripQueryAndHash(details.route);
    const properties =
      details.properties === undefined ? undefined : capProperties(details.properties);
    if (properties !== undefined) entry.properties = properties;

    if (entry.beforeSignIn) {
      appendToSignedOutBuffer(entry);
      return;
    }
    queue = [...queue, entry].slice(-MAX_QUEUED_ENTRIES);
    scheduleFlush();
  }

  return { record, flush };
}
